<template>
  <div class="table-v2">
    <div class="table-v2__wrap">
      <table class="table-v2__table table table-hover bg-white mb-0">
        <thead>
          <tr v-if="hasGroupRow" class="table-v2__grouprow">
            <th
              v-if="checkbox"
              class="table-v2__group-empty table-v2__th--check"
            ></th>
            <th
              v-for="(cell, gIdx) in groupHeaderCells"
              :key="'group-' + gIdx"
              :colspan="cell.colspan"
              :class="cell.headerClass"
            >
              {{ cell.label }}
            </th>
          </tr>
          <tr class="table-v2__colrow">
            <th
              v-if="checkbox"
              class="table-v2__th table-v2__th--check border-top-0"
            >
              <label
                v-if="selectAllHeader"
                class="table-v2__checkbox-wrap"
                :title="isAllSelected ? 'Désélectionner tout' : 'Sélectionner tout'"
              >
                <input
                  type="checkbox"
                  class="table-v2__checkbox"
                  :checked="isAllSelected"
                  :indeterminate.prop="isIndeterminate"
                  :disabled="!selectableRows.length"
                  @change="toggleAll($event)"
                />
              </label>
            </th>
            <th
              v-for="entry in visibleEntries"
              :key="'header-' + entry.index"
              class="table-v2__th border-top-0"
              :class="getGroupCellClass(entry.header.group)"
              :style="entry.header.size ? 'width:' + entry.header.size : ''"
            >
              <span v-if="entry.header.icon_class">
                <i :class="entry.header.icon_class" :title="entry.header.title || entry.header.label"></i>
              </span>
              <span v-else>{{ entry.header.label }}</span>
              <Tooltip
                v-if="entry.header.tooltip_value"
                :text="entry.header.tooltip_value"
                :width="'auto'"
              >
                <i
                  class="d-inline ml-2 cursor-pointer text-secondary fa"
                  :class="entry.header.tooltip_icon"
                ></i>
              </Tooltip>
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(row, trkey) in data">
          <tr
            v-if="notselect || row.role_id == undefined || row.role_id == currentUser.role_id"
            :key="trkey"
            :class="{ 'table-v2__row--selected': checkbox && isRowSelected(row) }"
          >
            <td v-if="checkbox" class="table-v2__td--check">
              <label class="table-v2__checkbox-wrap">
                <input
                  type="checkbox"
                  class="table-v2__checkbox"
                  :checked="isRowSelected(row)"
                  @change="toggleRow(row)"
                  @click.stop
                />
              </label>
            </td>
            <td
              v-for="entry in visibleEntries"
              :key="'row-' + entry.index"
              :class="[entry.header.class, getGroupCellClass(entry.header.group)]"
              :style="entry.header.percentage ? 'width:' + entry.header.percentage : ''"
            >
              <span v-for="col in entry.colg" :key="colKey(col, entry.index)">
                <span v-if="col.callOnly && !isCallTask(row)" class="table-v2__na">n/a</span>
                <template v-else>
                <!-- Texto simple -->
                <Tooltip
                  v-if="row[col.tooltip] && col.type != 'emit'"
                  :text="getValue(row, col, 'tooltip')"
                  :width="entry.header.size"
                >
                  <span
                    :class="{ 'table-v2__truncate': !!col.truncate }"
                    :style="truncateStyle(col)"
                    >{{ displayCellText(row, col) }}</span
                  >
                </Tooltip>

                <span
                  v-if="isPlainTextCell(col, row)"
                  :class="[col.class, { 'text-primary font-weight-bold h6': row.totalRow, 'table-v2__truncate': !!col.truncate }]"
                  :style="[col.style, truncateStyle(col)]"
                >
                  <span v-if="col.canBeHidden == 'yes' && row.show && row.show == 'no'">* * *</span>
                  <span v-else>{{ displayCellText(row, col) }}</span>
                </span>

                <!-- type: stackedLink -->
                <div v-if="col.type == 'stackedLink'" class="table-v2__stack">
                  <span
                    class="table-v2__link"
                    @click="emitnow(col, row[col.value_emit], trkey)"
                  >{{ row[col.value_label] }}</span>
                  <div class="table-v2__subtxt">{{ row[col.sub_value] }}</div>
                </div>

                <!-- type: stackedClient -->
                <div v-if="col.type == 'stackedClient'" class="table-v2__stack">
                  <span
                    class="table-v2__link"
                    @click="emitnow(col, row[col.value_emit], trkey)"
                  >{{ row[col.value_label] }}</span>
                  <div class="table-v2__subtxt">
                    <span
                      v-if="col.showStatusDot"
                      class="table-v2__cstat-dot"
                      :style="getClientStatusDotStyle(row, col)"
                    ></span>
                    {{ row[col.sub_value] }}
                  </div>
                </div>

                <!-- type: stackedText -->
                <div v-if="col.type == 'stackedText'" class="table-v2__stack">
                  <span :class="col.primary_class || 'table-v2__primary-txt'">{{ row[col.primary_value] }}</span>
                  <div v-if="getStackedSubText(row, col)" class="table-v2__subtxt">{{ getStackedSubText(row, col) }}</div>
                </div>

                <!-- type: stackedDateTime -->
                <div v-if="col.type == 'stackedDateTime'" class="table-v2__stack">
                  <span class="table-v2__primary-txt table-v2__primary-txt--normal">
                    {{ formatStackedDate(row[col.value]) }}
                  </span>
                  <div class="table-v2__subtxt">{{ formatStackedTime(row[col.value]) }}</div>
                </div>

                <!-- type: taskTypeIcon -->
                <div
                  v-if="col.type == 'taskTypeIcon'"
                  class="table-v2__typeico-wrap"
                >
                  <span :class="getTaskTypeIcon(row, col).wrapClass">
                    <i :class="getTaskTypeIcon(row, col).icon"></i>
                  </span>
                </div>

                <!-- type: meetingAiCard -->
                <template v-if="col.type == 'meetingAiCard'">
                  <div class="table-v2__mtg-ai-wrap">
                    <template v-if="getMeetingAiCard(row, col).type === 'no-meeting'">
                      <Tooltip text="No meeting" :width="'auto'">
                        <span class="table-v2__mtg-no">
                          <i class="icon-calendar-times-o"></i>
                        </span>
                      </Tooltip>
                    </template>
                    <div
                      v-else-if="getMeetingAiCard(row, col).type === 'dash'"
                      class="table-v2__muted"
                    >—</div>
                    <Tooltip
                      v-else
                      :text="getMeetingAiCard(row, col).tooltip"
                      :width="'auto'"
                    >
                      <div :class="['table-v2__mtg', getMeetingAiCard(row, col).cardClass]">
                        <span class="table-v2__mtg-dt">
                          <i :class="getMeetingAiCard(row, col).iconClass"></i>
                          {{ getMeetingAiCard(row, col).date }}<template v-if="getMeetingAiCard(row, col).time"> · {{ getMeetingAiCard(row, col).time }}</template>
                        </span>
                        <span class="table-v2__mtg-att">
                          <i class="icon-user"></i>
                          {{ getMeetingAiCard(row, col).attendee }}
                        </span>
                      </div>
                    </Tooltip>
                    <button
                      v-if="shouldShowMeetingAiRerun(row, col)"
                      type="button"
                      class="btn btn-primary btn-sm themed-button effect-btn ml-1 px-2 py-0"
                      title="Relancer Meeting AI"
                      @click="$emit(col.rerunEmit, row[col.value_emit || 'notification_id'])"
                    >↺</button>
                  </div>
                </template>

                <!-- type: meetingAiComparison -->
                <template v-if="col.type == 'meetingAiComparison'">
                  <Tooltip
                    v-if="getMeetingAiComparison(row, col).state === 'mismatch'"
                    :text="getMeetingAiComparison(row, col).tooltip"
                    :width="'auto'"
                  >
                    <span class="table-v2__ai-check table-v2__ai-check--bad">
                      <i class="icon-cross"></i>
                    </span>
                  </Tooltip>
                  <span v-else class="table-v2__muted">—</span>
                </template>

                <!-- type: meetingResult -->
                <template v-if="col.type == 'meetingResult'">
                  <template v-if="getMeetingResultState(row, col) === 'linked-card'">
                    <div :class="['table-v2__mtg', getMeetingLinkedTaskCard(row, col).cardClass]">
                      <span class="table-v2__mtg-dt">
                        <i :class="getMeetingLinkedTaskCard(row, col).iconClass"></i>
                        {{ getMeetingLinkedTaskCard(row, col).date }} · {{ getMeetingLinkedTaskCard(row, col).time }}
                      </span>
                      <span class="table-v2__mtg-att">
                        <i class="icon-user"></i>
                        {{ getMeetingLinkedTaskCard(row, col).attendee }}
                      </span>
                    </div>
                  </template>
                  <button
                    v-else-if="getMeetingResultState(row, col) === 'create-button'"
                    class="table-v2__createbtn"
                    @click="$emit(col.emit, row)"
                  >
                    <i class="icon-calendar-plus-o"></i>
                    Créer le meeting
                  </button>
                  <Tooltip
                    v-else
                    text="No meeting"
                    :width="'auto'"
                  >
                    <span class="table-v2__mtg-no">
                      <i class="icon-calendar-times-o"></i>
                    </span>
                  </Tooltip>
                </template>

                <!-- type: emit -->
                <span
                  v-if="col.type == 'emit' && shouldShowEmit(col, row)"
                  :class="[col.class, { 'table-v2__scriptbtn': col.scriptBtn }]"
                  :title="col.title || ''"
                  @click="emitnow(col, row[col.value_emit], trkey)"
                >
                  <i v-if="col.icon != undefined" :class="col.icon"></i>
                  <span
                    v-if="col.label != undefined"
                    :title="col.textShort && col.label.length > col.textShort ? col.label : ''"
                  >
                    {{ col.textShort ? formatLabel(col.label, col.textShort) : col.label }}
                  </span>
                  <span
                    v-else-if="col.value_label != undefined"
                    :title="
                      col.textShort && row[col.value_label].length > col.textShort
                        ? row[col.value_label]
                        : ''
                    "
                  >
                    {{
                      col.textShort
                        ? formatLabel(row[col.value_label], col.textShort)
                        : row[col.value_label]
                    }}
                  </span>
                </span>

                <!-- type: itemAndActionButton -->
                <template v-if="col.type == 'itemAndActionButton'">
                  <span 
                    :class="['table-v2__tag', getItemBadgeTagClass(row, col)]"
                  >
                    {{ getItemBadgeLabel(row, col) }}
                  </span>
                  <button
                      v-if="row[col.value]"
                      type="button"
                      class="btn btn-primary btn-sm themed-button effect-btn ml-1 px-2 py-0"
                      title="Relancer Meeting AI"
                      @click="$emit(col.rerunEmit, row[col.value_emit || 'notification_id'])"
                    >↺</button>
                </template> 

                <!-- type: contactPerson — iniciales + nombre + LinkedIn (sin foto externa por ahora) -->
                <template v-if="col.type == 'contactPerson'">
                  <div class="table-v2__person">
                    <div class="table-v2__avatar">
                      <span class="table-v2__avatar-initials">{{
                        contactInitials(row[col.name])
                      }}</span>
                    </div>
                    <span class="table-v2__person-name" :title="row[col.name]">{{
                      row[col.name] || "—"
                    }}</span>
                    <a
                      v-if="row[col.linkedin]"
                      :href="normalizeExternalUrl(row[col.linkedin])"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="table-v2__linkedin"
                      @click.stop
                    >
                      <i class="icon-linkedin"></i>
                    </a>
                  </div>
                </template>

                <!-- type: companyInitial — inicial + nombre + dominio -->
                <template v-if="col.type == 'companyInitial'">
                  <div class="table-v2__company">
                    <span class="table-v2__co-initial">{{
                      companyInitial(row[col.name])
                    }}</span>
                    <div class="table-v2__stack">
                      <span class="table-v2__co-name" :title="row[col.name]">{{
                        truncateText(row[col.name], col.truncate || 28) || "—"
                      }}</span>
                      <span
                        v-if="row[col.domain]"
                        class="table-v2__subtxt"
                        :title="formatDomainDisplay(row[col.domain])"
                        >{{
                          truncateText(
                            formatDomainDisplay(row[col.domain]),
                            col.truncate || 28
                          )
                        }}</span
                      >
                    </div>
                  </div>
                </template>

                <!-- type: emailVerifyStatus — email + estado de verificación -->
                <template v-if="col.type == 'emailVerifyStatus'">
                  <router-link
                    v-if="lockEmails"
                    to="/upgrade"
                    class="px-1 py-0 btn btn-sm btn-outline-primary"
                    @click.native.stop
                  >
                    Débloquer
                  </router-link>
                  <div v-else class="table-v2__email">
                    <span
                      class="table-v2__email-ico"
                      :class="'table-v2__email-ico--' + emailVerifyKey(row[col.statusField])"
                      :title="emailVerifyLabel(row[col.statusField])"
                    >
                      <i
                        v-if="emailVerifyKey(row[col.statusField]) === 'valide'"
                        class="icon-checkmark"
                      ></i>
                      <i
                        v-else-if="emailVerifyKey(row[col.statusField]) === 'accept_all'"
                        class="icon-warning"
                      ></i>
                      <i
                        v-else-if="emailVerifyKey(row[col.statusField]) === 'not_working'"
                        class="icon-cross"
                      ></i>
                      <i v-else class="icon-circle-o"></i>
                    </span>
                    <span
                      class="table-v2__email-text"
                      :class="{
                        'table-v2__email-text--strike':
                          emailVerifyKey(row[col.statusField]) === 'not_working',
                        'table-v2__email-text--muted':
                          emailVerifyKey(row[col.statusField]) === 'non_verifie',
                      }"
                      :title="row[col.value] || ''"
                      >{{ row[col.value] || "—" }}</span
                    >
                  </div>
                </template>

                <!-- type: phoneOrEmpty -->
                <template v-if="col.type == 'phoneOrEmpty'">
                  <span
                    v-if="hasPhoneValue(row[col.value])"
                    class="table-v2__phone"
                    >{{ row[col.value] }}</span
                  >
                  <span v-else class="table-v2__muted">Non renseigné</span>
                </template>

                <!-- type: contactStatutBadge — libellé business_contact_status_id (15 états) -->
                <template v-if="col.type == 'contactStatutBadge'">
                  <span
                    class="table-v2__statut"
                    :class="'table-v2__statut--s' + contactStatutMeta(row).id"
                    :title="contactStatutMeta(row).label"
                  >
                    <span class="table-v2__statut-dot"></span>
                    {{ contactStatutMeta(row).label }}
                  </span>
                </template>

                <!-- type: linkedOrder — punto de estado + nombre de order clicable -->
                <template v-if="col.type == 'linkedOrder'">
                  <div v-if="row[col.value_label]" class="table-v2__order">
                    <span
                      class="table-v2__order-dot"
                      :style="{ background: orderStateColor(row[col.stateField || 'linked_order_state']) }"
                    ></span>
                    <span
                      class="table-v2__order-link"
                      @click="emitnow(col, row[col.value_emit], trkey)"
                    >{{ row[col.value_label] }}</span>
                  </div>
                  <span v-else class="table-v2__muted">—</span>
                </template>

                <!-- type: deletionStatus — badge de estado + subtexto de condición -->
                <template v-if="col.type == 'deletionStatus'">
                  <div class="table-v2__stack">
                    <span
                      :class="['table-v2__status-badge', statusBadgeClass(row[col.value])]"
                    >
                      <span class="table-v2__status-dot"></span>
                      {{ statusBadgeLabel(row[col.value]) }}
                    </span>
                    <div v-if="row[col.sub_value]" class="table-v2__subtxt">{{ row[col.sub_value] }}</div>
                  </div>
                </template>

                <!-- type: scheduledDeletion — fecha límite + D-x + info de días añadidos por AM -->
                <template v-if="col.type == 'scheduledDeletion'">
                  <div v-if="row[col.deadline_field] || row[col.days_field] != null" class="table-v2__sched">
                    <span v-if="row[col.deadline_field]" class="table-v2__sched-date">{{ row[col.deadline_field] }}</span>
                    <span
                      v-if="row[col.days_field] != null"
                      :class="['table-v2__sched-days', scheduledDeletionUrgencyClass(row[col.urgency_field])]"
                    >D-{{ row[col.days_field] }}</span>
                    <span v-if="row[col.added_field]" class="table-v2__sched-added">{{ row[col.added_field] }}</span>
                  </div>
                  <span v-else class="table-v2__muted">—</span>
                </template>

                <!-- type: itemBadge -->
                <template v-if="col.type == 'itemBadge'">
                  <span v-if="row.totalRow" class="text-primary font-weight-bold h6">
                    {{ row[col.value] }}
                  </span>

                  <Tooltip
                    v-else-if="
                      row[col.value] != 'empty' &&
                      !col.format &&
                      !col.role &&
                      shouldDisplayItemBadge(row, col) &&
                      getBadgeTooltip(row, col)
                    "
                    :text="getBadgeTooltip(row, col)"
                    :width="'auto'"
                  >
                    <span
                      :class="['table-v2__tag', getItemBadgeTagClass(row, col)]"
                      @click="col.emit ? $emit(col.emit, row.id) : null"
                    >{{ getItemBadgeLabel(row, col) }}</span>
                  </Tooltip>
                  <span
                    v-else-if="
                      row[col.value] != 'empty' &&
                      !col.format &&
                      !col.role &&
                      shouldDisplayItemBadge(row, col)
                    "
                    :class="['table-v2__tag', getItemBadgeTagClass(row, col)]"
                    @click="col.emit ? $emit(col.emit, row.id) : null"
                  >{{ getItemBadgeLabel(row, col) }}</span>
                  <span
                    v-else-if="col.callOnly && isCallTask(row)"
                    class="table-v2__muted"
                  >—</span>
                </template>

                <!-- type: btnIfNotNull -->
                <template v-if="col.type == 'btnIfNotNull'">
                  <span v-if="!col.format">
                    <button
                      class="btn btn-primary btn-sm themed-button effect-btn w-40"
                      v-if="row[col.value_if] != null"
                      @click="$emit(col.emit, row[col.btnId])"
                    >
                      <span class="text-center">{{ col.name }}</span>
                    </button>
                  </span>
                  <span v-if="col.format == 3">
                    <button
                      v-if="
                        row[col.value_if] ==
                          (col.value_equals != null ? col.value_equals : col.value) &&
                        (!col.adminOnly || currentUser.role_id == 1)
                      "
                      :class="col.class || 'btn btn-primary btn-sm themed-button effect-btn w-40'"
                      @click="$emit(col.emit, row[col.value_emit || col.btnId])"
                    >
                      <i v-if="col.icon" :class="col.icon" class="mr-1"></i>
                      <span class="text-center">{{ col.label || col.name }}</span>
                    </button>
                  </span>
                </template>

                <!-- type: CreateMeetingButton -->
                <template v-if="col.type == 'CreateMeetingButton'">
                  <button
                    v-if="isCallTask(row)"
                    class="table-v2__createbtn"
                    @click="$emit(col.emit, row)"
                    :disabled="
                      !row[col.value1] ||
                      row[col.value1] === '' ||
                      row[col.value1] == 'No meeting' ||
                      row[col.value2] == null ||
                      row[col.value2] === '' ||
                      Number(row[col.value2]) > 0
                    "
                  >
                    <i class="icon-calendar-plus-o"></i>
                    Créer le meeting
                  </button>
                </template>

                <!-- type: multiple -->
                <template v-if="col.type == 'multiple'">
                  <span v-for="(element, condIdx) in col.conditions" :key="'cond-' + condIdx">
                    <span
                      v-if="element.type == 'button' && row[element.value_if] == element.value"
                      class="btn btn-sm btn-primary"
                      @click="handleType2(element, row)"
                    >
                      <span class="text-center">{{ element.name }}</span>
                    </span>

                    <span v-if="element.type == 'audio'">
                      <template
                        v-if="
                          row[element.value_source_type] === 'elevenlabs' &&
                          row[element.value_conversation_id]
                        "
                      >
                        <audio
                          :id="'audio-' + row[element.value_conversation_id]"
                          controls
                          src="data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQAAAAA="
                          style="width: 300px; height: 20px"
                          @play="onElevenLabsPlay(row[element.value_conversation_id], $event)"
                        ></audio>
                      </template>
                      <template v-else>
                        <audio
                          v-if="row[element.value_audio]"
                          :src="row[element.value_audio]"
                          style="width: 300px; height: 20px"
                          controls
                        ></audio>
                      </template>
                    </span>

                    <span
                      v-if="element.type == 'icon' && row[element.value_if] == element.value"
                      @click="handleType2(element, row)"
                    >
                      <i :class="element.class"></i>
                    </span>

                    <span class="ml-1"
                      v-if="element.type == 'text'"
                    >
                      {{ row[element.value] }}
                    </span>

                    <span v-if="element.type == 'audio2'">
                      <template                      
                        v-if="
                          row[element.value_source_type] === 'elevenlabs' &&
                          row[element.value_conversation_id]
                        "
                      >
                      
                        <audio
                          :id="'audio-' + row[element.value_conversation_id]"
                          controls
                          src="data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQAAAAA="
                          style="width: 300px; height: 20px"
                          @play="onElevenLabsPlay(row[element.value_conversation_id], $event)"
                        ></audio>
                      </template>
                      <template v-else>
                        <audio
                          v-if="row[element.value_audio]"
                          :src="row[element.value_audio]"
                          style="width: 300px; height: 20px"
                          controls
                        ></audio>
                      </template>
                    </span>

                    <span
                      v-if="element.type == 'bubble' && row[element.value_if]"
                      @click="handleType2(element, row)"
                    >
                      <span
                        class="ml-1 p-0 circle bg-primary text-center text-white"
                        :class="element.class"
                      >
                        {{ row[element.value] }}
                      </span>
                    </span>

                    <Tooltip
                      v-for="(badge, badgeIdx) in element.badges"
                      v-if="shouldShowBadge(row, badge)"
                      :key="'mb-' + badgeIdx"
                      :class="badge.class_tooltip"
                      :text="badge.badge_tooltip"
                      :width="'auto'"
                    >
                      <span
                        :class="['table-v2__tag', 'ml-2', getLooseBadgeTagClass(badge)]"
                      >
                        <i v-if="badge.badge_icon" :class="badge.badge_icon"></i>
                        <span>{{ badge.badge_label }}</span>
                      </span>
                    </Tooltip>
                  </span>
                </template>

                <!-- Badges sueltos (ej. Contact établi) -->
                <Tooltip
                  v-for="(badge, bIdx) in col.badges"
                  v-if="shouldShowBadge(row, badge)"
                  :key="'badge-' + bIdx"
                  :class="badge.class_tooltip"
                  :text="badge.badge_tooltip"
                  :width="'auto'"
                >
                  <span :class="['table-v2__tag', getLooseBadgeTagClass(badge)]">
                    <i v-if="badge.badge_icon" :class="badge.badge_icon"></i>
                    {{ badge.badge_label }}
                  </span>
                </Tooltip>
                <span
                  v-if="col.callOnly && isCallTask(row) && col.badges && !hasMatchingLooseBadge(row, col)"
                  class="table-v2__muted"
                >—</span>
                </template>
              </span>
            </td>
          </tr>
          </template>
        </tbody>
      </table>
    </div>

    <div v-if="legends.length" class="table-v2__legend">
      <span v-for="(legend, legIdx) in legends" :key="'legend-' + legIdx" class="table-v2__legend-item">
        <span
          class="table-v2__legend-dot"
          :style="{
            background: legend.color,
            border: legend.border ? '0.5px solid ' + legend.border : 'none',
          }"
        ></span>
        {{ legend.label }}
      </span>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import Tooltip from "@/components/Common/Tooltip.vue";
import { adminService } from "@/_services";

export default {
  name: "TableV2",
  components: {
    Tooltip,
  },
  props: {
    headers: {
      type: Array,
      required: true,
    },
    cols: {
      type: Array,
      required: true,
    },
    data: {
      type: Array,
      required: true,
    },
    /**
     * Definición de los grupos de columnas.
     * Ejemplo:
     * {
     *   contexte: { label: 'Contexte', headerClass: 'g-context', cellClass: 'col-context' },
     *   booker: { label: 'Travail du booker', headerClass: 'g-human', cellClass: 'col-human' },
     * }
     * Cada header puede referenciar un grupo con header.group = 'contexte'.
     */
    columnGroups: {
      type: Object,
      default: () => ({}),
    },
    /**
     * Leyenda debajo de la tabla.
     * Ejemplo: [{ label: 'Travail du booker', color: '#EEEEFC', border: '#4B4BE1' }]
     */
    legends: {
      type: Array,
      default: () => [],
    },
    notselect: {
      type: Boolean,
      default: true,
    },
    checkbox: {
      type: Boolean,
      default: false,
    },
    /**
     * Nombre del campo de la fila cuyo valor se guardará en el array
     * de seleccionados (p. ej. 'id', 'task_id', 'uuid', etc.).
     * Solo aplica cuando checkbox === true.
     */
    checkboxField: {
      type: String,
      default: "id",
    },
    /**
     * Array de valores actualmente seleccionados. Compatible con `:selected.sync`
     * y emite también el evento `selection-change` con el array completo.
     */
    selected: {
      type: Array,
      default: () => [],
    },
    selectAllHeader: {
      type: Boolean,
      default: false,
    },
    model: {
      type: [Object, Array],
      default: () => ({}),
    },
    fieldsLoading: {
      type: Boolean,
      default: false,
    },
    myvalues: {
      type: Array,
      default: () => [],
    },
    /**
     * Si true, la columna emailVerifyStatus muestra CTA de upgrade
     * en lugar del email (mismo criterio que Table.emailLock / SearchResult.lockEmails).
     */
    lockEmails: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      definitionsOfTypes: [
        "stackedLink",
        "stackedClient",
        "stackedText",
        "stackedDateTime",
        "taskTypeIcon",
        "meetingAiCard",
        "meetingAiComparison",
        "meetingResult",
        "emit",
        "itemBadge",
        "btnIfNotNull",
        "CreateMeetingButton",
        "multiple",
        "itemAndActionButton",
        "contactPerson",
        "companyInitial",
        "emailVerifyStatus",
        "phoneOrEmpty",
        "contactStatutBadge",
        "linkedOrder",
        "deletionStatus",
        "scheduledDeletion",
      ],
    };
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.authentication.user,
    }),

    visibleEntries() {
      return this.headers
        .map((header, index) => ({
          header,
          index,
          colg: this.cols[index] || [],
        }))
        .filter(
          ({ header, colg }) =>
            this.isHeaderVisible(header) && this.hasPermissionColumn(colg)
        );
    },

    selectableRows() {
      return (this.data || []).filter(
        (row) =>
          this.notselect ||
          row.role_id == undefined ||
          row.role_id == this.currentUser.role_id
      );
    },

    selectableKeys() {
      return this.selectableRows
        .map((row) => row[this.checkboxField])
        .filter((v) => v !== undefined && v !== null);
    },

    isAllSelected() {
      if (!this.selectableKeys.length) return false;
      return this.selectableKeys.every((key) =>
        this.selected.some((s) => s == key)
      );
    },

    isIndeterminate() {
      if (!this.selectableKeys.length) return false;
      const someSelected = this.selectableKeys.some((key) =>
        this.selected.some((s) => s == key)
      );
      return someSelected && !this.isAllSelected;
    },

    hasGroupRow() {
      return this.visibleEntries.some((entry) => entry.header.group);
    },

    groupHeaderCells() {
      const cells = [];
      const entries = this.visibleEntries;
      let i = 0;

      while (i < entries.length) {
        const groupId = entries[i].header.group || null;

        if (!groupId) {
          cells.push({
            colspan: 1,
            label: "",
            headerClass: "table-v2__group-empty",
          });
          i += 1;
          continue;
        }

        let colspan = 1;
        while (
          i + colspan < entries.length &&
          entries[i + colspan].header.group === groupId
        ) {
          colspan += 1;
        }

        const groupDef = this.columnGroups[groupId] || {};
        cells.push({
          colspan,
          label: groupDef.label || "",
          headerClass: ["table-v2__group-cell", groupDef.headerClass].filter(Boolean),
        });
        i += colspan;
      }

      return cells;
    },

    formatLabel() {
      return (label, maxText) => {
        if (!label || typeof label !== "string") return label;
        return label.length > maxText ? label.substr(0, maxText) + "..." : label;
      };
    },

    labelValue() {
      return (value, col, row) => {
        if (col.colors && col.colors.find((el) => el.id == value)) {
          return col.colors.find((el) => el.id == value).label;
        }
        return row || "...";
      };
    },

    bgColor() {
      return (value, col) => {
        const match = col.colors && col.colors.find((el) => el.id == value);
        return match ? match.bgColor : "black";
      };
    },

    textColor() {
      return (value, col) => {
        const match = col.colors && col.colors.find((el) => el.id == value);
        return match ? match.textColor : "white";
      };
    },

    fontSize() {
      return (value, col) => {
        const match = col.colors && col.colors.find((el) => el.id == value);
        return match && match.fontSize ? match.fontSize : "12px";
      };
    },
  },
  beforeDestroy() {
    document.querySelectorAll('audio[id^="audio-"]').forEach((el) => {
      if (el.src && el.src.startsWith("blob:")) {
        URL.revokeObjectURL(el.src);
      }
    });
  },
  methods: {
    colKey(col, index) {
      return (col.type || "plain") + "-" + (col.value || col.value_label || index);
    },

    isHeaderVisible(header) {
      if (header.role_id != undefined && header.role_id != this.currentUser.role_id) {
        return false;
      }
      if (header.visible != undefined && !header.visible) {
        return false;
      }
      if (
        header.permission != undefined &&
        !this.$store.getters["authentication/hasPermissions"](header.permission)
      ) {
        return false;
      }
      return true;
    },

    shouldShowMeetingAiRerun(row, col) {
      if (!col.rerunEmit || !row[col.value_emit || "notification_id"]) {
        return false;
      }

      if (row.entity_type === "email_sent") {
        return true;
      }

      if (row.entity_type === "ringover_event") {
        return String(row.has_transcription) === "1";
      }

      return false;
    },

    hasPermissionColumn(colg) {
      return (
        colg[0] == undefined ||
        colg[0].role_id == undefined ||
        colg[0].role_id == this.currentUser.role_id
      );
    },

    getGroupCellClass(groupId) {
      if (!groupId || !this.columnGroups[groupId]) {
        return "";
      }
      return this.columnGroups[groupId].cellClass || "";
    },

    isDefinedCellType(col) {
      return col.type && this.definitionsOfTypes.includes(col.type);
    },

    isPlainTextCell(col, row) {
      if (row[col.tooltip]) return false;
      if (this.isDefinedCellType(col)) return false;
      if (col.badges && col.badges.length) return false;
      if (col.loading && this.fieldsLoading) return false;
      return true;
    },

    isCallTask(row) {
      if (row.task_type_id === "coldphoning") return true;
      return row.task_type === "Passer un appel";
    },

    formatStackedDate(value) {
      if (!value) return "";
      const parts = String(value).trim().split(" ");
      const datePart = parts[0] || "";
      const bits = datePart.split("-");
      if (bits.length !== 3) return datePart;
      return `${bits[2]}/${bits[1]}/${bits[0]}`;
    },

    formatStackedTime(value) {
      if (!value) return "";
      const parts = String(value).trim().split(" ");
      return parts[1] || "";
    },

    formatShortTime(value) {
      const time = this.formatStackedTime(value) || String(value || "").trim();
      return time ? time.substring(0, 5) : "";
    },

    getStackedSubText(row, col) {
      const raw = row[col.sub_value];
      if (!raw) return "";
      let text = String(raw);
      if (col.sub_format === "datetime") {
        const date = this.formatStackedDate(raw);
        const time = this.formatStackedTime(raw);
        text = time ? `${date} ${time}` : date;
      }
      return col.sub_prefix ? `${col.sub_prefix}${text}` : text;
    },

    getTaskTypeIcon(row, col) {
      const id = row[col.taskTypeIdField || "task_type_id"];
      const title = row[col.taskTypeField || "task_type"];

      if (id === "coldphoning" || title === "Passer un appel") {
        return { icon: "icon-phone", wrapClass: "table-v2__typeico table-v2__typeico--phone" };
      }
      if (id === "manualanswer" || title === "Répondre au mail") {
        return { icon: "icon-envelope-o", wrapClass: "table-v2__typeico table-v2__typeico--mail" };
      }

      const iconMap = {
        reminder: "icon-bell-o",
        absent: "icon-user-times",
        calendar: "icon-calendar",
        cancelled: "icon-ban",
      };
      return {
        icon: iconMap[id] || "icon-tasks",
        wrapClass: "table-v2__typeico table-v2__typeico--generic",
      };
    },

    parseMeetingResponse(raw) {
      if (!raw || raw === "empty" || !String(raw).trim()) return null;
      try {
        let parsed = typeof raw === "string" ? JSON.parse(raw.trim()) : raw;
        if (typeof parsed === "string") parsed = JSON.parse(parsed);
        return parsed && typeof parsed === "object" ? parsed : null;
      } catch (e) {
        return null;
      }
    },

    getProspectDisplay(parsed) {
      if (!parsed) return null;
      const name = [parsed.prospect_firstname, parsed.prospect_lastname]
        .filter(Boolean)
        .join(" ")
        .trim();
      if (name) return name;
      if (parsed.prospect_email) return parsed.prospect_email;
      return null;
    },

    formatMeetingDate(dateStr) {
      if (!dateStr) return null;
      const bits = String(dateStr).split("-");
      if (bits.length !== 3) return dateStr;
      return `${bits[2]}/${bits[1]}/${bits[0]}`;
    },

    formatMeetingTooltip(parsed) {
      if (!parsed) return "";
      const lines = [];
      const add = (label, val) => {
        if (val != null && val !== "") lines.push(`${label}: ${val}`);
      };
      add("Qualification", parsed.qualification);
      add("Date", this.formatMeetingDate(parsed.meeting_date));
      add("Heure", parsed.meeting_time);
      add("Prénom", parsed.prospect_firstname);
      add("Nom", parsed.prospect_lastname);
      add("Email", parsed.prospect_email);
      add("Téléphone", parsed.prospect_phone);
      add("Entreprise", parsed.prospect_company);
      add("Poste", parsed.prospect_job_title);
      add("Site web", parsed.prospect_website);
      if (parsed.description) add("Description", parsed.description);
      return lines.join("\n");
    },

    getClientStatusColorEntry(row, col) {
      if (!col.statusColors || !Array.isArray(col.statusColors)) {
        return null;
      }

      const statusId = col.statusIdField ? row[col.statusIdField] : null;
      if (statusId != null && statusId !== "") {
        const byId = col.statusColors.find((el) => String(el.id) === String(statusId));
        if (byId) return byId;
      }

      const statusLabel = row[col.sub_value];
      if (statusLabel) {
        return col.statusColors.find(
          (el) => String(el.label).toLowerCase() === String(statusLabel).toLowerCase()
        );
      }

      return null;
    },

    getClientStatusDotStyle(row, col) {
      const entry = this.getClientStatusColorEntry(row, col);
      const fallback = "#9a9aab";
      return { background: entry && entry.bgColor ? entry.bgColor : fallback };
    },

    normalizeMeetingStatus(status) {
      if (!status || status === "empty") return null;
      const s = String(status).trim().toLowerCase().replace(/_/g, " ");
      if (s === "no meeting" || s === "nomeeting") return "no_meeting";
      if (s.includes("reschedule")) return "rescheduled_meeting";
      if (s.includes("new") && s.includes("meeting")) return "new_meeting";
      return status;
    },

    isNoMeetingStatus(status) {
      const normalized = this.normalizeMeetingStatus(status);
      return !normalized || normalized === "no_meeting";
    },

    isRescheduledMeetingStatus(status) {
      return this.normalizeMeetingStatus(status) === "rescheduled_meeting";
    },

    getMeetingTaskNumber(row, col) {
      const field = col.taskNumberField || "meeting_task_number";
      const raw = row[field];
      if (raw == null || raw === "") return 0;
      const num = Number(raw);
      return Number.isNaN(num) ? 0 : num;
    },

    hasValidMeetingResponse(raw) {
      return !!(raw && raw !== "empty" && String(raw).trim());
    },

    hasDetectedAiMeeting(row, col) {
      const { status, response } = this.getMeetingAiFields(row, col);
      if (this.isNoMeetingStatus(status)) {
        return false;
      }
      if (!this.hasValidMeetingResponse(response)) {
        return false;
      }
      const parsed = this.parseMeetingResponse(response);
      if (!parsed) {
        return false;
      }
      const date = parsed.meeting_date;
      return !!(date != null && String(date).trim() !== "");
    },

    getMeetingAiFields(row, col) {
      const callResponse = row[col.responseField || "calification_call_response"];
      const callStatus = row[col.statusField || "calification_call"];

      if (this.hasValidMeetingResponse(callResponse)) {
        return { status: callStatus, response: callResponse };
      }

      const emailStatusField = col.emailStatusField || "calification";
      const emailResponseField = col.emailResponseField || "calification_response";
      const emailResponse = row[emailResponseField];
      if (this.hasValidMeetingResponse(emailResponse)) {
        return { status: row[emailStatusField], response: emailResponse };
      }

      return { status: callStatus, response: callResponse };
    },

    getMeetingResultState(row, col) {
      const taskNum = this.getMeetingTaskNumber(row, col);
      const taskDate = row[col.taskDateField || "meeting_task_date"];

      if (taskNum > 0 && taskDate) {
        return "linked-card";
      }
      if (this.hasDetectedAiMeeting(row, col)) {
        return "create-button";
      }
      return "no-meeting";
    },

    getMeetingLinkedTaskCard(row, col) {
      const dateStr = row[col.taskDateField || "meeting_task_date"];
      const attendee = row[col.contactField || "buasiness_contact_name"] || "";
      return {
        type: "card",
        date: this.formatStackedDate(dateStr),
        time: this.formatShortTime(dateStr),
        attendee,
        cardClass: "table-v2__mtg--create",
        iconClass: "icon-calendar-plus-o",
      };
    },

    normalizeComparableText(value) {
      return String(value || "").trim().replace(/\s+/g, " ").toLowerCase();
    },

    normalizeComparableDate(value) {
      if (!value) return "";
      const raw = String(value).trim();
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
        const bits = raw.split("/");
        return `${bits[2]}-${bits[1]}-${bits[0]}`;
      }
      return raw.split(" ")[0] || raw;
    },

    normalizeComparableTime(value) {
      if (!value) return "";
      return String(value).trim().substring(0, 5);
    },

    getMeetingAiComparison(row, col) {
      const taskNum = this.getMeetingTaskNumber(row, col);
      const meetingDateTime = row[col.taskDateField || "meeting_task_date"];

      if (taskNum <= 0 || !meetingDateTime) {
        return { state: "empty", tooltip: "" };
      }

      const { response } = this.getMeetingAiFields(row, col);
      const parsed = this.parseMeetingResponse(response);
      const aiDate = parsed ? this.normalizeComparableDate(parsed.meeting_date) : "";
      const aiTime = parsed ? this.normalizeComparableTime(parsed.meeting_time) : "";
      const aiAttendee = parsed ? this.normalizeComparableText(this.getProspectDisplay(parsed)) : "";
      const meetingDate = this.normalizeComparableDate(meetingDateTime);
      const meetingTime = this.normalizeComparableTime(this.formatShortTime(meetingDateTime));
      const meetingAttendee = this.normalizeComparableText(row[col.contactField || "buasiness_contact_name"]);

      const differences = [];
      if (!aiDate || aiDate !== meetingDate) differences.push(`Date AI: ${aiDate || "-"} / Meeting: ${meetingDate || "-"}`);
      if (!aiTime || aiTime !== meetingTime) differences.push(`Heure AI: ${aiTime || "-"} / Meeting: ${meetingTime || "-"}`);
      if (!aiAttendee || !meetingAttendee || aiAttendee !== meetingAttendee) {
        differences.push(`Attendee AI: ${aiAttendee || "-"} / Meeting: ${meetingAttendee || "-"}`);
      }

      return differences.length
        ? { state: "mismatch", tooltip: differences.join("\n") }
        : { state: "match", tooltip: "" };
    },

    getMeetingAiCard(row, col) {
      if (!this.hasDetectedAiMeeting(row, col)) {
        return { type: "no-meeting", tooltip: null };
      }

      const { status, response } = this.getMeetingAiFields(row, col);
      const parsed = this.parseMeetingResponse(response);
      const date = parsed ? this.formatMeetingDate(parsed.meeting_date) : null;
      const rawTime = parsed && parsed.meeting_time != null ? String(parsed.meeting_time).trim() : "";
      const time = rawTime || null;
      const attendee = parsed ? this.getProspectDisplay(parsed) : null;

      if (!date || !attendee) {
        return { type: "dash", tooltip: null };
      }

      const isReschedule = this.isRescheduledMeetingStatus(status);
      return {
        type: "card",
        date,
        time,
        attendee,
        tooltip: this.formatMeetingTooltip(parsed),
        cardClass: isReschedule ? "table-v2__mtg--reschedule" : "table-v2__mtg--create",
        iconClass: isReschedule ? "icon-calendar" : "icon-calendar-plus-o",
      };
    },

    findColorEntry(colors, raw) {
      if (!colors || !Array.isArray(colors) || raw == null || raw === "empty") {
        return null;
      }
      const direct = colors.find((el) => String(el.id) === String(raw));
      if (direct) return direct;

      const normalizedRaw = String(raw).trim().toLowerCase().replace(/_/g, " ");
      return (
        colors.find(
          (el) => String(el.id).trim().toLowerCase().replace(/_/g, " ") === normalizedRaw
        ) ||
        colors.find(
          (el) => String(el.label).trim().toLowerCase().replace(/_/g, " ") === normalizedRaw
        ) ||
        null
      );
    },

    getItemBadgeLabel(row, col) {
      const raw = row[col.value];
      const entry = this.findColorEntry(col.colors, raw);
      if (entry && entry.label) return entry.label;
      return raw || "";
    },

    getItemBadgeTagClass(row, col) {
      const raw = row[col.value];
      const entry = this.findColorEntry(col.colors, raw);
      const label = entry ? entry.label : String(raw || "");
      return this.mapLabelToTagVariant(label, col.value, entry);
    },

    getLooseBadgeTagClass(badge) {
      return this.mapLabelToTagVariant(badge.badge_label, badge.badge_if, null);
    },

    mapLabelToTagVariant(label, field, entry) {
      const l = String(label || "").toLowerCase();

      if (field === "link_status_temperature_id" || field === "link_status_temperature_id") {
        if (l.includes("urgent")) return "table-v2__tag--red";
        if (l.includes("positive")) return "table-v2__tag--green";
        if (l.includes("reschedule")) return "table-v2__tag--amber";
        if (l.includes("cold")) return "table-v2__tag--cold";
        if (l.includes("calendar")) return "table-v2__tag--cyan";
        return "table-v2__tag--cyan";
      }

      if (field === "task_output") {
        if (l.includes("meeting booked")) return "table-v2__tag--green";
        if (l.includes("call back")) return "table-v2__tag--amber";
        if (l.includes("no answer") || l.includes("no phone")) return "table-v2__tag--grey";
        if (l.includes("wrong number") || l.includes("no interest") || l.includes("neutral")) {
          return "table-v2__tag--red";
        }
        return "table-v2__tag--red";
      }

      if (field === "calification") {
        const n = l.replace(/_/g, " ");
        if (n.includes("new") && n.includes("meeting")) return "table-v2__tag--green";
        if (n.includes("rescheduled")) return "table-v2__tag--amber";
        return "table-v2__tag--grey";
      }

      if (field === "task_fake") {
        if (entry && entry.id === 1) return "table-v2__tag--red";
        if (entry && entry.id === 2) return "table-v2__tag--amber";
        if (entry && entry.id === 3) return "table-v2__tag--green";
      }

      if (field === "result" || l.includes("conversation")) {
        if (l.includes("conversation")) return "table-v2__tag--green";
        if (l.includes("wrong number")) return "table-v2__tag--red";
        if (l.includes("no interaction")) return "table-v2__tag--grey";
      }

      if (field === "transcription_ai_result") {
        if (l.includes("no pitch") || l.includes("no need")) return "table-v2__tag--green";
        if (l.includes("other")) return "table-v2__tag--amber";
        return "table-v2__tag--cyan";
      }

      if (field === "qualification" || field === "calification_call") {
        if (l.includes("meeting booked") || l.includes("new meeting")) return "table-v2__tag--green";
        if (l.includes("rescheduled")) return "table-v2__tag--amber";
        return "table-v2__tag--grey";
      }

      if (l.includes("failed") || l.includes("cancelled")) return "table-v2__tag--red";
      if (l.includes("meeting") && l.includes("booked")) return "table-v2__tag--green";
      return "table-v2__tag--grey";
    },

    shouldShowBadge(row, badge) {
      if (!row[badge.badge_if]) return false;
      if (badge.badge_value === undefined) return true;
      return row[badge.badge_if] == badge.badge_value;
    },

    hasMatchingLooseBadge(row, col) {
      if (!col.badges || !col.badges.length) return false;
      return col.badges.some((badge) => this.shouldShowBadge(row, badge));
    },

    shouldShowEmit(col, row) {
      return (
        col.value_if === undefined ||
        (col.value_if !== undefined && col.value_if == "null" && !row[col.value]) ||
        col.value_if == row[col.value] ||
        (col.value_multiple_if !== undefined &&
          this.evalIf(row, col.value_multiple_if))
      );
    },

    badgeStyle(row, col) {
      return (
        "border-radius: 5px; padding: 1px 4px; background-color:" +
        this.bgColor(this.getValue(row, col), col) +
        "; color:" +
        this.textColor(this.getValue(row, col), col) +
        "; font-size:" +
        this.fontSize(this.getValue(row, col), col)
      );
    },

    async onElevenLabsPlay(conversationId, ev) {
      const audioEl = ev.target;
      if (audioEl.dataset.loaded === "1") return;
      ev.preventDefault();
      audioEl.pause();
      const blob = await adminService.getElevenLabsAudio(conversationId);
      audioEl.src = URL.createObjectURL(blob);
      audioEl.dataset.loaded = "1";
      audioEl.play();
    },

    getHtmlContent(row, col) {
      if (row[col.value2]) {
        if (col.only_value === "true") {
          return (
            this.labelValue(this.getValue(row, col), col, this.getValue(row, col)) +
            " " +
            row[col.value2]
          );
        }
        return (
          this.labelValue(this.getValue(row, col), col, this.getValue(row, col)) +
          " (" +
          row[col.value2] +
          ")"
        );
      }
      const colaux = col.colors && col.colors.find((el) => el.id == this.getValue(row, col));
      if (colaux && colaux.label) {
        return colaux.label;
      }
      return "";
    },

    evalIf(row, value_multiple_if) {
      let result = true;
      value_multiple_if.forEach((v) => {
        if (v.condition == "null") {
          if (row[v.value]) result = false;
        } else if (Array.isArray(v.condition)) {
          if (!v.condition.includes(row[v.value])) result = false;
        } else if (v.condition != row[v.value]) {
          result = false;
        }
      });
      return result;
    },

    emitnow(col, value, index) {
      if (!value) {
        value = index;
      }
      if (col.filter_emit !== undefined) {
        value = col.filter_emit;
      }
      if (col.action !== undefined) {
        this.$emit(col.emit, col.action, value);
      } else {
        this.$emit(col.emit, value);
      }
    },

    getValue(row, col, tooltip) {
      let value = col.value;
      if (tooltip) {
        value = col.tooltip;
      }
      if (col.type === "itemBadge") return row[col.value];
      if (col.show_text === "false" && !col.show_total) return "";
      if (col.getvalue) {
        value = "";
        if (row[col.value]) {
          this.myvalues.forEach((thevalue) => {
            if (thevalue.id == row[col.value]) {
              value = thevalue.name;
            }
          });
        }
        return value;
      }
      if (col.filter) {
        return this.$options.filters[col.filter](row[value]);
      }
      if (col.translate !== undefined && col.translate) {
        return this.$t(col.translate + "." + row[value]);
      }
      if (col.value2 !== undefined && row[value] && row[value][col.value2]) {
        return row[value][col.value2];
      }
      if (Array.isArray(row[value]) && row[value].length == 0) {
        return col.value_empty;
      }
      const raw = row[value];
      if (raw !== undefined && raw !== null && raw !== "") return raw;
      return col.value_empty;
    },

    formatResponseTooltip(raw) {
      const parsed = this.parseMeetingResponse(raw);
      if (!parsed) return typeof raw === "string" ? raw : "";
      return this.formatMeetingTooltip(parsed);
    },

    getBadgeTooltip(row, col) {
      const value = this.getValue(row, col);
      if (row[col.value] == "empty" || col.format || col.role) return null;
      if (col.colors && Array.isArray(col.colors)) {
        const colorItem = col.colors.find((el) => String(el.id) === String(value));
        if (colorItem) {
          if (colorItem.tooltipRowField) {
            const fromRow = row[colorItem.tooltipRowField];
            if (fromRow != null && fromRow !== "") {
              const formatted = this.formatResponseTooltip(fromRow);
              if (formatted) return formatted;
            }
            if (colorItem.tooltipFallbackKey) return this.$t(colorItem.tooltipFallbackKey);
          }
          if (colorItem.tooltip) return colorItem.tooltip;
        }
      }
      if (col.tooltipWhenValue && col.tooltipText && value == col.tooltipWhenValue) {
        return col.tooltipText;
      }
      return null;
    },

    shouldDisplayItemBadge(row, col) {
      if (row.totalRow) return false;
      const raw = row[col.value];
      if (raw === "empty" || raw === null || raw === undefined) return false;
      if (!col.colors || !Array.isArray(col.colors)) return true;
      const entry = this.findColorEntry(col.colors, raw);
      if (!entry) return false;
      const bg = (entry.bgColor || "").toLowerCase();
      const noLabel = !entry.label || String(entry.label).trim() === "";
      const transparentBg =
        bg === "transparent" || bg === "rgba(0,0,0,0)" || bg === "rgba(0, 0, 0, 0)";
      if (transparentBg && noLabel) return false;
      return true;
    },

    handleType2(col, row) {
      if (col.type2 == "emit") {
        this.$emit(col.emit, row);
      }
    },

    isRowSelected(row) {
      const key = row[this.checkboxField];
      if (key === undefined || key === null) return false;
      return this.selected.some((s) => s == key);
    },

    emitSelection(next) {
      this.$emit("update:selected", next);
      this.$emit("selection-change", next);
    },

    toggleRow(row) {
      const key = row[this.checkboxField];
      if (key === undefined || key === null) return;
      const next = this.selected.slice();
     
      const idx = next.findIndex((s) => s == key);
      if (idx > -1) {
        next.splice(idx, 1);
      } else {
        next.push(key);
      }

      this.emitSelection(next);
    },

    toggleAll(event) {
      const shouldSelect = event && event.target ? event.target.checked : !this.isAllSelected;
      if (shouldSelect) {
        const merged = this.selected.slice();
        this.selectableKeys.forEach((key) => {
          if (!merged.some((s) => s == key)) merged.push(key);
        });
        this.emitSelection(merged);
      } else {
        const next = this.selected.filter(
          (s) => !this.selectableKeys.some((key) => key == s)
        );
        this.emitSelection(next);
      }
    },

    displayCellText(row, col) {
      const raw = this.getValue(row, col);
      if (raw == null) return "";
      return this.truncateText(String(raw), Number(col.truncate));
    },

    truncateText(value, max) {
      if (value == null) return "";
      const text = String(value);
      const limit = Number(max);
      if (!limit || !Number.isFinite(limit) || limit <= 0 || text.length <= limit) {
        return text;
      }
      return text.slice(0, limit).trimEnd() + "…";
    },

    truncateStyle(col) {
      if (!col || !col.truncate) return null;
      const width = col.truncateWidth || "180px";
      return {
        maxWidth: width,
        display: "inline-block",
        verticalAlign: "middle",
      };
    },

    contactInitials(name) {
      if (!name || typeof name !== "string") return "?";
      const parts = name.trim().split(/\s+/).filter(Boolean);
      if (!parts.length) return "?";
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
      return (
        parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
      ).toUpperCase();
    },

    companyInitial(name) {
      if (!name || typeof name !== "string") return "?";
      const trimmed = name.trim();
      return trimmed ? trimmed.charAt(0).toUpperCase() : "?";
    },

    normalizeExternalUrl(url) {
      if (!url) return "#";
      const s = String(url).trim();
      if (!s) return "#";
      if (/^https?:\/\//i.test(s)) return s;
      return "https://" + s;
    },

    formatDomainDisplay(domain) {
      if (!domain) return "";
      return String(domain)
        .replace(/^https?:\/\//i, "")
        .replace(/\/$/, "");
    },

    hasPhoneValue(phone) {
      if (phone == null) return false;
      const s = String(phone).trim();
      return s !== "" && s !== "-";
    },

    emailVerifyKey(statusEmail) {
      if (statusEmail === null || statusEmail === undefined || statusEmail === "") {
        return "non_verifie";
      }
      const n = Number(statusEmail);
      if (n === 5) return "valide";
      if (n === 3) return "accept_all";
      if (n === 58) return "not_working";
      return "non_verifie";
    },

    emailVerifyLabel(statusEmail) {
      const key = this.emailVerifyKey(statusEmail);
      const labels = {
        valide: "Valide",
        accept_all: "Accept All",
        not_working: "Not working",
        non_verifie: "Non vérifié",
      };
      return labels[key] || "Non vérifié";
    },

    contactStatutMeta(row) {
      const id = Number(row.business_contact_status_id);
      const labels = {
        1: "New",
        2: "Waiting",
        3: "Analyzed",
        4: "Deleted",
        5: "Bounce",
        6: "Duplicated",
        7: "Do not contact",
        8: "Not working here / Malformed email",
        9: "Waiting to be enriched",
        10: "Unblocked",
        11: "Meeting booked",
        12: "Email unavailable",
        13: "Person doesnt exists",
        14: "Email not found",
        15: "Catch all",
      };
      if (labels[id]) {
        return { id, label: labels[id] };
      }
      return { id: 0, label: "—" };
    },

    orderStateColor(state) {
      const colors = {
        running: "#0f6e56",
        preparation: "#4b4be1",
        stopped: "#854f0b",
        draft: "#9a9aab",
        archived: "#5a5a6e",
        unknown: "#c62828",
      };
      return colors[state] || "#9a9aab";
    },

    statusBadgeLabel(status) {
      const labels = {
        in_use: "In use",
        available: "Available",
        pending_delete: "Pending delete",
        not_applicable: "N/A",
      };
      return labels[status] || status || "—";
    },

    statusBadgeClass(status) {
      const classes = {
        in_use: "table-v2__status-badge--green",
        available: "table-v2__status-badge--grey",
        pending_delete: "table-v2__status-badge--red",
        not_applicable: "table-v2__status-badge--grey",
      };
      return classes[status] || "table-v2__status-badge--grey";
    },

    scheduledDeletionUrgencyClass(urgency) {
      if (urgency === "red") return "table-v2__sched-days--red";
      if (urgency === "amber") return "table-v2__sched-days--amber";
      return "";
    },
  },
};
</script>

<style scoped>
.table-v2 {
  --tv2-surface: #ffffff;
  --tv2-surface-2: #f4f4f6;
  --tv2-border: #e8e8ee;
  --tv2-text: #1c1c28;
  --tv2-text-2: #5a5a6e;
  --tv2-text-3: #9a9aab;
  --tv2-primary: #4b4be1;
  --tv2-human: #4b4be1;
  --tv2-agent: #0e6e7a;
  --tv2-radius: 14px;
}

.table-v2__wrap {
  overflow-x: auto;
  border: 0.5px solid var(--tv2-border);
  border-radius: var(--tv2-radius);
  background: var(--tv2-surface);
}

.table-v2__table {
  border-collapse: collapse;
  width: 100%;
  min-width: 1200px;
}

.table-v2__grouprow th {
  padding: 9px 11px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  text-align: left;
  border-bottom: 0.5px solid var(--tv2-border);
}

.table-v2__group-empty {
  background: var(--tv2-surface-2);
}

.table-v2__group-cell {
  background: var(--tv2-surface-2);
  color: var(--tv2-text-3);
}

.table-v2__grouprow .g-context {
  background: var(--tv2-surface-2);
  color: var(--tv2-text-3);
}

.table-v2__grouprow .g-human {
  background: #eeeefc;
  color: var(--tv2-human);
}

.table-v2__grouprow .g-agent {
  background: #def0f2;
  color: var(--tv2-agent);
}

.table-v2__grouprow .g-result {
  background: var(--tv2-surface-2);
  color: var(--tv2-text-3);
}

.table-v2__colrow th,
.table-v2__th {
  font-size: 10.5px;
  font-weight: 700;
  color: var(--tv2-text-3);
  text-align: left;
  padding: 11px;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: var(--tv2-surface);
}

.table-v2__table tbody td {
  font-size: 12.5px;
  padding: 13px 11px;
  vertical-align: middle;
  white-space: nowrap;
  border-top: 0.5px solid var(--tv2-border);
}

.table-v2__table tbody tr:hover {
  background: var(--tv2-surface-2);
}

.table-v2__table tbody tr:hover .col-human {
  background: rgba(75, 75, 225, 0.05);
}

.table-v2__table tbody tr:hover .col-agent {
  background: rgba(14, 110, 122, 0.055);
}

.col-human {
  background: rgba(75, 75, 225, 0.025);
}

.col-agent {
  background: rgba(14, 110, 122, 0.03);
}

.table-v2__legend {
  display: flex;
  gap: 16px;
  margin-top: 10px;
  flex-wrap: wrap;
}

.table-v2__legend-item {
  font-size: 11px;
  color: var(--tv2-text-2);
  display: flex;
  align-items: center;
  gap: 6px;
}

.table-v2__legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 3px;
  flex-shrink: 0;
}

.table-v2__na {
  font-size: 11px;
  color: var(--tv2-text-3);
  font-style: italic;
}

.table-v2__stack {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.table-v2__link {
  color: var(--tv2-primary);
  font-weight: 500;
  cursor: pointer;
}

.table-v2__link:hover {
  text-decoration: underline;
}

.table-v2__primary-txt {
  font-weight: 500;
}

.table-v2__primary-txt--normal {
  font-weight: 400;
}

.table-v2__subtxt {
  font-size: 10.5px;
  color: var(--tv2-text-3);
  display: flex;
  align-items: center;
  gap: 3px;
}

.table-v2__cstat-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.table-v2__muted {
  color: var(--tv2-text-3);
}

.table-v2__typeico-wrap {
  text-align: center;
}

.table-v2__typeico {
  width: 28px;
  height: 28px;
  border-radius: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.table-v2__typeico--mail {
  background: #eeeefc;
  color: var(--tv2-primary);
}

.table-v2__typeico--phone {
  background: #d6f1f5;
  color: #0e6e7a;
}

.table-v2__typeico--generic {
  background: var(--tv2-surface-2);
  color: var(--tv2-text-2);
}

.table-v2__scriptbtn {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: var(--tv2-surface-2);
  border: 0.5px solid var(--tv2-border);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.table-v2__scriptbtn:hover {
  background: #eeeefc;
  border-color: var(--tv2-primary);
  color: var(--tv2-primary);
}

.table-v2__tag {
  font-size: 10.5px;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: 6px;
  white-space: nowrap;
  display: inline-block;
}

.table-v2__tag--green {
  background: #e1f5ee;
  color: #0f6e56;
}

.table-v2__tag--amber {
  background: #faeeda;
  color: #854f0b;
}

.table-v2__tag--red {
  background: #fcebeb;
  color: #a32d2d;
}

.table-v2__tag--grey {
  background: #f1efe8;
  color: #444441;
}

.table-v2__tag--cold {
  background: #2b2b2b;
  color: #fff;
}

.table-v2__tag--cyan {
  background: #d6f1f5;
  color: #0e6e7a;
}

.table-v2__mtg {
  display: inline-flex;
  flex-direction: column;
  gap: 2px;
  border-radius: 8px;
  padding: 6px 9px;
  min-width: 128px;
}

.table-v2__mtg--create {
  background: #e1f5ee;
  border-left: 3px solid #0f6e56;
}

.table-v2__mtg--reschedule {
  background: #faeeda;
  border-left: 3px solid #854f0b;
}

.table-v2__mtg-dt {
  font-size: 10.5px;
  font-weight: 600;
  color: var(--tv2-text);
  display: flex;
  align-items: center;
  gap: 4px;
}

.table-v2__mtg-att {
  font-size: 10px;
  color: var(--tv2-text-2);
  display: flex;
  align-items: center;
  gap: 4px;
}

.table-v2__mtg-no {
  width: 28px;
  height: 28px;
  border-radius: 7px;
  background: #f1efe8;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--tv2-text-3);
}

.table-v2__mtg-ai-wrap {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.table-v2__ai-check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.table-v2__ai-check--bad {
  background: #fdeaea;
  color: #dc3545;
}

.table-v2__ai-check--ok {
  background: #eaf7ee;
  color: #28a745;
}

.table-v2__createbtn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  padding: 7px 12px;
  border-radius: 7px;
  background: var(--tv2-primary);
  color: #fff;
  border: none;
  cursor: pointer;
}

.table-v2__createbtn:hover:not(:disabled) {
  background: #3c3489;
}

.table-v2__createbtn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.table-v2__th--check,
.table-v2__td--check {
  width: 36px;
  padding-left: 14px;
  padding-right: 6px;
  text-align: center;
}

.table-v2__checkbox-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin: 0;
  line-height: 0;
}

.table-v2__checkbox {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1.5px solid var(--tv2-border);
  background: var(--tv2-surface);
  cursor: pointer;
  position: relative;
  transition: background 0.15s ease, border-color 0.15s ease;
  margin: 0;
}

.table-v2__checkbox:hover:not(:disabled) {
  border-color: var(--tv2-primary);
}

.table-v2__checkbox:checked {
  background: var(--tv2-primary);
  border-color: var(--tv2-primary);
}

.table-v2__checkbox:checked::after {
  content: "";
  position: absolute;
  left: 4px;
  top: 1px;
  width: 5px;
  height: 9px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.table-v2__checkbox:indeterminate {
  background: var(--tv2-primary);
  border-color: var(--tv2-primary);
}

.table-v2__checkbox:indeterminate::after {
  content: "";
  position: absolute;
  left: 2px;
  top: 6px;
  width: 9px;
  height: 2px;
  background: #fff;
  border-radius: 1px;
}

.table-v2__checkbox:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.table-v2__row--selected {
  background: rgba(75, 75, 225, 0.06);
}

.table-v2__row--selected:hover {
  background: rgba(75, 75, 225, 0.1) !important;
}

/* Contacts redesign cells */
.table-v2__person {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  max-width: 260px;
}

.table-v2__avatar {
  width: 32px;
  height: 32px;
  min-width: 32px;
  border-radius: 50%;
  overflow: hidden;
  background: #e8e8ee;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.table-v2__avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.table-v2__avatar-initials {
  font-size: 11px;
  font-weight: 700;
  color: #5a5a6e;
  line-height: 1;
}

.table-v2__person-name {
  font-weight: 600;
  color: var(--tv2-text);
  overflow: hidden;
  text-overflow: ellipsis;
}

.table-v2__truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 180px;
}

.table-v2__linkedin {
  color: #0a66c2;
  font-size: 14px;
  line-height: 1;
  text-decoration: none;
}

.table-v2__linkedin:hover {
  opacity: 0.85;
}

.table-v2__company {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  max-width: 240px;
}

.table-v2__co-initial {
  width: 32px;
  height: 32px;
  min-width: 32px;
  border-radius: 8px;
  background: #eef0f4;
  color: #3d3d4f;
  font-size: 13px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.table-v2__co-name {
  font-weight: 600;
  color: var(--tv2-text);
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

.table-v2__email {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 260px;
}

.table-v2__email-ico {
  width: 18px;
  height: 18px;
  min-width: 18px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
}

.table-v2__email-ico--valide {
  background: #eaf7ee;
  color: #1f7a3d;
}

.table-v2__email-ico--accept_all {
  background: #fff6e0;
  color: #b78105;
}

.table-v2__email-ico--not_working {
  background: #fdeaea;
  color: #c62828;
}

.table-v2__email-ico--non_verifie {
  background: #f0f0f3;
  color: #8a8a9a;
}

.table-v2__email-text {
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--tv2-text);
}

.table-v2__email-text--strike {
  text-decoration: line-through;
  color: #c62828;
}

.table-v2__email-text--muted {
  color: var(--tv2-text-3);
}

.table-v2__phone {
  color: var(--tv2-text);
}

.table-v2__muted {
  color: var(--tv2-text-3);
}

.table-v2__statut {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}

.table-v2__statut-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
}

.table-v2__statut--s1 {
  background: #e8f4fd;
  color: #1565c0;
}
.table-v2__statut--s2 {
  background: #eef2ff;
  color: #4b4be1;
}
.table-v2__statut--s3 {
  background: #eaf7ee;
  color: #1f7a3d;
}
.table-v2__statut--s4 {
  background: #f0f0f3;
  color: #6b6b7b;
}
.table-v2__statut--s5 {
  background: #fdeaea;
  color: #c62828;
}
.table-v2__statut--s6 {
  background: #f5f0ff;
  color: #6b4fa0;
}
.table-v2__statut--s7 {
  background: #fff3e0;
  color: #c26a00;
}
.table-v2__statut--s8 {
  background: #fce4ec;
  color: #ad1457;
}
.table-v2__statut--s9 {
  background: #e0f7fa;
  color: #00838f;
}
.table-v2__statut--s10 {
  background: #e8f5e9;
  color: #2e7d32;
}
.table-v2__statut--s11 {
  background: #f0e9fb;
  color: #6b3fa0;
}
.table-v2__statut--s12 {
  background: #fff8e1;
  color: #f57f17;
}
.table-v2__statut--s13 {
  background: #efebe9;
  color: #5d4037;
}
.table-v2__statut--s14 {
  background: #ffebee;
  color: #b71c1c;
}
.table-v2__statut--s15 {
  background: #fffde7;
  color: #f9a825;
}
.table-v2__statut--s0 {
  background: #f0f0f3;
  color: #9a9aab;
}

.table-v2__order {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  max-width: 320px;
}

.table-v2__order-dot {
  width: 7px;
  height: 7px;
  min-width: 7px;
  border-radius: 50%;
}

.table-v2__order-link {
  color: #0f6e56;
  font-weight: 600;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.table-v2__order-link:hover {
  text-decoration: underline;
}

.table-v2__status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
}

.table-v2__status-badge .table-v2__status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
}

.table-v2__status-badge--green {
  color: #0f6e56;
}

.table-v2__status-badge--red {
  color: #c62828;
}

.table-v2__status-badge--grey {
  color: #6b6b7b;
}

.table-v2__sched {
  display: inline-flex;
  flex-direction: column;
  gap: 3px;
  align-items: flex-end;
}

.table-v2__sched-date {
  font-size: 12px;
  font-weight: 600;
  color: var(--tv2-text);
}

.table-v2__sched-days {
  font-size: 10.5px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 6px;
  background: #f1efe8;
  color: #444441;
  white-space: nowrap;
}

.table-v2__sched-days--red {
  background: #fcebeb;
  color: #a32d2d;
}

.table-v2__sched-days--amber {
  background: #faeeda;
  color: #854f0b;
}

.table-v2__sched-added {
  font-size: 10.5px;
  color: var(--tv2-text-3);
}
</style>
